import { errorToast, toast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import type { ConfirmButtonProps } from "@/v2-backoffice-client/components/button/ConfirmButton"
import { useUserResetMfaMutation } from "@/v2-backoffice-client/user/resetMfa/user.resetMfa.mutation"
import { USER_RESET_MFA_DONE_TITLE } from "@/v2-backoffice-client/user/resetMfa/user.resetMfa.constants"

const RESET_MFA_CONFIRM =
  "Clear this user's registered MFA method(s) so they re-register on their next sign-in. Use this to hand MFA setup back to the user instead of configuring it for them. Existing sessions are unaffected."

export type UserResetMfaButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm" | "confirmContent" | "content"
> & {
  userId: number
  content?: ConfirmButtonProps["content"]
  onCompleted?(): void
}

// Surfaces resetUserMfa as a button (toolbar / MFA tab), the button counterpart
// to UserResetMfaMenuItem. Clearing the methods is what forces the user to
// re-enrol MFA themselves at next sign-in.
function UserResetMfaButton(props: UserResetMfaButtonProps) {
  const { userId, content, onCompleted, children, ...buttonProps } = props

  const [resetMfa, { loading, error }] = useUserResetMfaMutation({
    onCompleted() {
      toast({ variant: "success", title: USER_RESET_MFA_DONE_TITLE })
      onCompleted?.()
    },
  })
  errorToast(error, { title: "Failed to reset MFA" })

  return (
    <ConfirmButton
      variant="warning"
      iconSrc="shield-check"
      content={
        content ??
        "Clear the user's registered MFA methods so they re-register on their next sign-in. Existing sessions are unaffected."
      }
      confirmContent={RESET_MFA_CONFIRM}
      confirmVariant="warning"
      disabled={loading}
      onConfirm={() => {
        resetMfa({ variables: { id: userId } }).catch(() => {})
      }}
      {...buttonProps}
    >
      {children ?? "Reset MFA"}
    </ConfirmButton>
  )
}

export default UserResetMfaButton
